import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChangeEvent, KeyboardEvent, forwardRef, useEffect, useRef, useState } from "react";
import Loading from "../Loading";
import useThrottle from "src/hooks/useThrottle";
import { twMerge } from "tailwind-merge";

interface InputProps {
  placeholder: string;
  defaultValue?: string;
  textArea?: boolean;
  onClick: (value: string) => void;
  loading?: boolean;
  label?: string;
  labelClass?: string;
  inputClass?: string;
  type?: string;
  search?: boolean;
  delay?: number;
  icon?: JSX.Element;
  disabled?: boolean;
  disabledInput?: boolean;
  resetButton?: boolean;
  require?: boolean;
  error?: boolean;
  style?: React.CSSProperties;
  inputWrapperClass?: string;
  resetHandle?: () => void;
  handleValue?: (value: string) => void;
  heightAuto?: boolean;
  name?: string;
  emptyValue?: boolean;
  id?: string;
  autoFocus?: boolean;
}

export const SearchUI = forwardRef<any, InputProps>(
  (
    {
      placeholder,
      defaultValue = "",
      textArea = false,
      onClick,
      loading = false,
      label,
      labelClass,
      delay = 500,
      icon,
      inputClass,
      disabled = false,
      disabledInput = false,
      resetButton = false,
      resetHandle,
      handleValue,
      search = true,
      error = false,
      type = "search",
      require = false,
      inputWrapperClass,
      style,
      heightAuto = false, // Новый проп для включения авто-изменения высоты
      name = "search",
      emptyValue = false,
      id = "search",
      autoFocus = false,
    }: InputProps,
    ref
  ) => {
    const [searchQuery, setSearchQuery] = useState(defaultValue);

    const throttledText = useThrottle(searchQuery, delay);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

    // Функция для изменения высоты textarea
    const adjustHeight = () => {
      if (textAreaRef.current && heightAuto) {
        textAreaRef.current.style.height = "auto"; // сброс высоты
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`; // установка новой высоты
      }
    };

    function inputPress(e: KeyboardEvent) {
      e.stopPropagation();

      if (e.key === "Enter") {
        onClick(searchQuery);
      }
    }

    // Обновляем значение и подстраиваем высоту
    function inputValue(value: string) {
      if (textArea) {
        setSearchQuery(JSON.parse(value));
      } else {
        setSearchQuery(value);
      }
      adjustHeight();
    }

    function resetValue() {
      setSearchQuery("");
      onClick(searchQuery);
      resetHandle && resetHandle();
    }

    useEffect(() => {
      setSearchQuery(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
      if (textAreaRef.current) {
        adjustHeight(); // Подстройка высоты при изменении defaultValue
      }
    }, [textAreaRef]);

    useEffect(() => {
      if (emptyValue || throttledText !== defaultValue) {
        onClick(throttledText);
      }
    }, [throttledText]);

    return (
      <div className={twMerge("w-full rounded-md shadow-sm", inputWrapperClass)}>
        {!!label && (
          <label htmlFor={id} className={twMerge("mb-2.5 flex flex-row justify-between ", labelClass)}>
            <span>{label}</span>
            {require && (
              <div className="flex flex-row justify-between">
                {error && <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>}
                <span className="text-[#BA0C2F]">*</span>
              </div>
            )}
          </label>
        )}
        <div className={twMerge(!!label ? "mt-2.5" : "", "relative")}>
          {textArea ? (
            <textarea
              autoFocus={autoFocus}
              ref={(el) => {
                textAreaRef.current = el;
                if (typeof ref === "function") ref(el);
                else if (ref) ref.current = el;
              }}
              className={twMerge(
                "block w-full rounded-md border-0 py-3 pl-5 pr-10 ring-[#DFE4EA] ring-1 ring-inset placeholder:text-[#9CA3AF] sm:text-base sm:leading-6 whitespace-pre-wrap",
                disabled ? "bg-[#E5E7EB] resize-none" : "",
                inputClass
              )}
              name={name}
              id={id}
              value={searchQuery}
              onChange={(event) => {
                inputValue(JSON.stringify(event.target.value));
              }}
              onKeyDown={inputPress}
              placeholder={disabled ? "" : placeholder}
              aria-invalid="true"
              disabled={disabled}
              style={style}
            />
          ) : (
            <input
              ref={ref}
              type={type}
              name={name}
              id={id}
              value={searchQuery}
              onChange={(event) => inputValue(event.target.value)}
              onKeyDown={inputPress}
              autoFocus={autoFocus}
              className={twMerge(
                "block w-full rounded-md border-0 py-3 pl-5 pr-10 ring-[#DFE4EA] ring-1 focus:ring-[#DFE4EA] focus:ring-1 focus:ring-inset placeholder:text-[#9CA3AF] sm:text-base sm:leading-6 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
                disabled ? "bg-[#E5E7EB]" : "",
                inputClass
              )}
              placeholder={disabled ? "" : placeholder}
              aria-invalid="true"
              disabled={disabled}
              style={style}
            />
          )}

          {!disabled && search ? (
            resetButton && searchQuery.trim() !== '' ? (
              <button
                disabled={loading}
                className="flex absolute inset-y-0 right-0 z-10 items-center pr-3 cursor-pointer outline-none"
                onClick={resetValue}
                type="button"
              >
                {loading ? <Loading /> : <XMarkIcon className="w-5 h-5" color="#6B7280" />}
              </button>
            ) : (
              <button
                disabled={loading}
                className="flex absolute inset-y-0 right-0 items-center pr-3 cursor-pointer outline-none"
                onClick={() => onClick(searchQuery)}
                type="button"
              >
                {loading ? <Loading /> : <MagnifyingGlassIcon className="w-5 h-5" aria-hidden="true" />}
              </button>
            )
          ) : null}
        </div>
      </div>
    );
  }
);

export function SearchListUI({
  placeholder,
  defaultValue = "",
  onClick,
  loading = false,
}: {
  placeholder: string;
  defaultValue?: string;
  onClick: (value: string) => void;
  loading?: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState(defaultValue);

  function inputPress(e: KeyboardEvent) {
    e.stopPropagation();

    if (e.key === "Enter") {
      onClick(searchQuery);
    }
  }

  function inputValue(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.value !== "") {
      setSearchQuery(e.target.value);
      return;
    }
    onClick("");
    setSearchQuery("");
  }

  return (
    <div className="relative w-full rounded-md shadow-sm">
      <input
        type="search"
        name="search"
        id="search"
        value={searchQuery}
        onChange={inputValue}
        onKeyDown={inputPress}
        className="block w-full rounded-md border-0 py-3 pl-5 pr-10 ring-[#DFE4EA] ring-1 ring-inset placeholder:text-[#9CA3AF] sm:text-base sm:leading-6"
        placeholder={placeholder}
        aria-invalid="true"
      />

      <button
        disabled={loading}
        className="flex absolute inset-y-0 right-0 items-center pr-3 cursor-pointer"
        onClick={() => onClick(searchQuery)}
      >
        {loading ? <Loading /> : <MagnifyingGlassIcon className="w-5 h-5" aria-hidden="true" />}
      </button>
    </div>
  );
}
